import {
  createContext,
  Dispatch,
  isValidElement,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { equals } from "ramda";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface INextGenHeaderContext {
  breadcrumbs: BreadcrumbItem[];
  title: ReactNode;
  actions: [ReactNode?, ReactNode?];
  rightPane: ReactNode;
  setBreadcrumbs: Dispatch<SetStateAction<BreadcrumbItem[]>>;
  setTitle: Dispatch<SetStateAction<ReactNode>>;
  setActions: Dispatch<SetStateAction<[ReactNode?, ReactNode?]>>;
  setRightPane: Dispatch<SetStateAction<ReactNode>>;
}

const compareReactNodes = (node1: ReactNode, node2: ReactNode): boolean => {
  if (isValidElement(node1) && isValidElement(node2)) {
    return node1.type === node2.type && equals(node1.props, node2.props);
  }
  return equals(node1, node2);
};

const NextGenHeaderContext = createContext<INextGenHeaderContext>({
  breadcrumbs: [],
  title: null,
  actions: [],
  rightPane: null,
  setBreadcrumbs: () => {},
  setTitle: () => {},
  setActions: () => {},
  setRightPane: () => {},
});

export const NextGenHeaderProvider = ({ children }: { children: ReactNode }) => {
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);
  const [title, setTitle] = useState<ReactNode>(null);
  const [actions, setActions] = useState<[ReactNode?, ReactNode?]>([]);
  const [rightPane, setRightPane] = useState<ReactNode>(null);

  return (
    <NextGenHeaderContext.Provider
      value={{
        breadcrumbs,
        setBreadcrumbs,
        title,
        setTitle,
        actions,
        setActions,
        rightPane,
        setRightPane,
      }}
    >
      {children}
    </NextGenHeaderContext.Provider>
  );
};

const useNextGenHeader = ({
  breadcrumbs: initialBreadcrumbs,
  title: initialTitle,
  actions: initialActions,
  rightPane: initialRightPane,
}: Partial<
  Pick<INextGenHeaderContext, "breadcrumbs" | "title" | "actions" | "rightPane">
> = {}) => {
  const context = useContext(NextGenHeaderContext);

  if (!context) {
    throw new Error("useNextGenHeader must be used within a NextGenHeaderProvider");
  }

  const { setBreadcrumbs, setTitle, setActions, setRightPane } = context;

  useEffect(() => {
    if (initialBreadcrumbs !== undefined && !equals(initialBreadcrumbs, context.breadcrumbs)) {
      setBreadcrumbs(initialBreadcrumbs);
    }
  }, [initialBreadcrumbs, context.breadcrumbs, setBreadcrumbs]);

  useEffect(() => {
    if (initialTitle !== undefined && !compareReactNodes(initialTitle, context.title)) {
      setTitle(initialTitle);
    }
  }, [initialTitle, context.title, setTitle]);

  useEffect(() => {
    if (initialActions !== undefined && !equals(initialActions, context.actions)) {
      setActions(initialActions);
    }
  }, [initialActions, context.actions, setActions]);

  useEffect(() => {
    if (initialRightPane !== undefined && !compareReactNodes(initialRightPane, context.rightPane)) {
      setRightPane(initialRightPane);
    }
  }, [initialRightPane, context.rightPane, setRightPane]);

  // Reset all header state when the page unmounts so stale values don't bleed
  // into pages that don't call useNextGenHeader.
  useEffect(() => {
    return () => {
      setBreadcrumbs([]);
      setTitle(null);
      setActions([]);
      setRightPane(null);
    };
  }, [setBreadcrumbs, setTitle, setActions, setRightPane]);

  return context;
};

export type { BreadcrumbItem };
export default useNextGenHeader;
